import { FormControl, Stack, Typography } from '@mui/material';
import { ConditionDatesForm } from '@retail/calculus/components';
import { useController, useFormContext } from 'react-hook-form';
import { FollowPriceSuggestionSchema } from './followPriceSuggestionSchema';
import { PriceSuggestionTypeWithCount } from '@retail/price-suggestion/types';
import { PriceSuggestionType } from '@retail/products/types';
import { useCallback, useMemo } from 'react';
import { usePriceSuggestionTFunction } from '@retail/price-suggestion/i18n';
import { HelperText } from '@shared/components';
import { PriceSuggestionPrioritization } from '../PriceSuggestionPriority/PriceSuggestionPrioritization';

interface Props {
  priceSuggestionTypes: PriceSuggestionTypeWithCount[];
}

export function FollowPriceSuggestionForm({ priceSuggestionTypes }: Props) {
  const t = usePriceSuggestionTFunction();

  const { control } = useFormContext<FollowPriceSuggestionSchema>();
  const priceSuggestionController = useController({ name: 'priceSuggestionPriority', control });

  const priceSuggestionPriority = useMemo(
    () => priceSuggestionController.field.value,
    [priceSuggestionController.field.value]
  );

  const updatePrizeSuggestionPrioritazion = useCallback(
    (newPrio: PriceSuggestionType[]) => priceSuggestionController.field.onChange(newPrio),
    [priceSuggestionController.field]
  );

  const getRadioItemValue = useCallback(
    (priceSuggestionType: PriceSuggestionType) => {
      const itemCount =
        priceSuggestionTypes.find(
          (priceSuggestion) => priceSuggestion.suggestionType === priceSuggestionType
        )?.count + '';
      return t('priceSuggestion.itemCount', { itemCount });
    },
    [priceSuggestionTypes, t]
  );

  return (
    <form>
      <Stack gap={3} pb={1}>
        {priceSuggestionTypes.length === 0 ? (
          <Typography px={3}>{t('priceSuggestion.noPriceSuggestion')}</Typography>
        ) : (
          <>
            <FormControl error={!!priceSuggestionController.fieldState.error} required>
              <PriceSuggestionPrioritization
                priceSuggestionPriority={priceSuggestionPriority}
                availablePriceSuggestions={priceSuggestionTypes.map((type) => type.suggestionType)}
                updatePriceSuggestionPrioritazion={updatePrizeSuggestionPrioritazion}
                getPriceTypeValue={getRadioItemValue}
              />

              {priceSuggestionController.fieldState.error && (
                <Stack px={3}>
                  <HelperText id={'pricesuggestion'} status={'error'}>
                    {t('priceSuggestion.error.selectPriceSuggestion')}
                  </HelperText>
                </Stack>
              )}
            </FormControl>
            <Stack px={3}>
              <ConditionDatesForm excludeToDate labelAlignment="column" />
            </Stack>
          </>
        )}
      </Stack>
    </form>
  );
}
